<template>
  <div :class="[kls, isPC ? '' : 'px-[35px]']" class="w-full h-full">
    <div :class="isPC ? 'max-w-[1180px] mx-auto rounded-[20px] overflow-hidden' : ''">
      <!-- 猜涨跌 -->
      <UpDownSession
        v-if="session && kind === SESSION_KIND.STOCK_RISE_FALL"
        :session="session"
        :market-stats="marketStats"
        :up-down-type="computedUpDownType"
        @select="handleSelect"
        @countdown-finish="refreshSession"
      >
        <template #stock-info>
          <PredictStockInfo
            class="mt-[28px]"
            :quote-snap-data="quoteSnapData"
            :up-down-type="computedUpDownType"
            :favorited="favorited"
            :lang="lang"
            @add-watch="handleAddWatch"
            @remove-watch="handleRemoveWatch"
            @stock-click="handleStockClick"
          />
        </template>
      </UpDownSession>

      <!-- 猜收盘价 -->
      <ClosePriceSession
        v-else-if="session && kind === SESSION_KIND.STOCK_CLOSING_PRICE"
        :session="session"
        :market-stats="marketStats"
        @click-cta="handleClosePriceCta"
        @countdown-finish="refreshSession"
      >
        <template #stock-info>
          <PredictStockInfo
            class="mt-[28px]"
            :quote-snap-data="quoteSnapData"
            :up-down-type="computedUpDownType"
            :favorited="favorited"
            :lang="lang"
            @add-watch="handleAddWatch"
            @remove-watch="handleRemoveWatch"
            @stock-click="handleStockClick"
          />
        </template>
      </ClosePriceSession>

      <!-- 预测事件 -->
      <EventSession
        v-else-if="session && kind === SESSION_KIND.EVENT_PREDICTION"
        :session="session"
        :market-stats="marketStats"
        :show-percent-ongoing="showPercentOngoing"
        @select="handleSelect"
        @countdown-finish="refreshSession"
      />

      <!-- 理由选择弹窗 -->
      <ReasonPopup
        v-if="session && session.reasonConfig"
        :show="showReasonPopup"
        :title="$t('reasonPopupTitle')"
        :submit-text="$t('reasonPopupSubmit')"
        :reasons="presetReasons"
        :default-code="lastReasonCode"
        @update:show="showReasonPopup = $event"
        @submit="handleReasonSubmit"
      />

      <!-- 猜收盘价弹窗 -->
      <ClosePricePopup
        v-if="session && kind === SESSION_KIND.STOCK_CLOSING_PRICE"
        :show="showClosePricePopup"
        :current-price="currentStockPrice"
        :default-value="lastClosePriceValue"
        :precision="closePricePrecision"
        :min="closePriceMin"
        :max="closePriceMax"
        :target-date="session.dataSource && session.dataSource.targetDate"
        @update:show="showClosePricePopup = $event"
        @submit="handleClosePriceSubmit"
      />
    </div>
  </div>
</template>

<script lang="ts">
import QuoteSnapshot from '@futuweb/quote-chart-ui/lib/v2/QuoteSnapshot/index.js';
import { computed, ref, provide, onMounted } from 'vue-demi';
import { use as fegoUse } from '@fegobull/webapi';
import { useFegoPageInfo, useUpDownType, UpDownTypes } from '@ark/web-shared/use';
import { SESSION_KIND, PREDICT_STATUS } from '../shared/types';
import type { PredictOption, PredictSession } from '../shared/types';
import type { MarketStatsResponse } from '@ark/predict-shared';
import { apiClient } from '../shared/api';
import { useSubmitGuess } from './hooks/useSubmitGuess';
import { useLogin } from '@ark/web-shared/use';
import { WORDING_KEY } from './hooks/useWording';
import EventSession from './components/event.vue';
import UpDownSession from './components/up-down.vue';
import ClosePriceSession from './components/close-price.vue';
import PredictStockInfo from './components/stock-info.vue';
import ReasonPopup from './components/reason-popup.vue';
import ClosePricePopup from './components/close-price-popup.vue';
import { fetchQuoteSnapshotWithCache, fetchWatchStatusWithCache, shouldFetchStockRelatedInfo } from './utils/stock-request';
import * as browser from '@futuweb/tool-browser';
import locales from './locales';
const __sfc_main = {};
__sfc_main.props = ['kind', 'marketId', 'stockId', 'stockLinkInternal', 'stockLinkExternal', 'upDownType', 'upDownTypeFollowClient', 'optionVarName', 'showPercentOngoing', 'useAnalyzeWording'];
__sfc_main.setup = (__props, __ctx) => {
  const getDisplayPrice = QuoteSnapshot.getDisplayPrice;
  const props = __props;
  provide(WORDING_KEY, !!props.useAnalyzeWording);
  const {
    useI18n,
    useVariable,
    useToast
  } = fegoUse;
  const $t = useI18n(locales);
  const Toast = useToast();
  const {
    kls,
    lang,
    isPC
  } = useFegoPageInfo();
  const {
    isLoggedIn,
    toLogin
  } = useLogin();
  const fegoVars = useVariable();
  const computedUpDownType = useUpDownType(props.upDownType != null ? props.upDownType : UpDownTypes.GreenUpRedDown, props.upDownTypeFollowClient != null ? props.upDownTypeFollowClient : true);
  const session = ref<PredictSession | null>(null);
  const quoteSnapData = ref<Record<string, unknown> | null>(null);
  const marketStats = ref<MarketStatsResponse | null>(null);
  const favorited = ref(false);
  const showReasonPopup = ref(false);
  const pendingOption = ref<PredictOption | null>(null);
  const lastReasonCode = ref<string | null>(null);
  const showClosePricePopup = ref(false);
  const lastClosePriceValue = ref<string | null>(null);
  const {
    submit
  } = useSubmitGuess({
    apiClient
  });
  const submitting = ref(false);
  const stockId = computed(() => {
    const ds = session.value && session.value.dataSource;
    return ds && ds.stockId || props.stockId || '';
  });

  /** 将选中选项的索引写入 fego 页面变量，供其他组件联动 */
  function setOptionVar(optionCode: string | null) {
    if (!props.optionVarName || !session.value) return;
    const options = session.value.answerSpec && session.value.answerSpec.options;
    if (!options || optionCode == null) {
      fegoVars[props.optionVarName] = '-1';
      return;
    }
    const idx = options.findIndex((o: any) => o.code === optionCode);
    fegoVars[props.optionVarName] = String(idx);
  }
  const presetReasons = computed(() => {
    const cfg = session.value && session.value.reasonConfig;
    if (!cfg || !cfg.presetReasons) return [];
    return cfg.presetReasons;
  });

  /** 公共前置校验：状态 → 修改次数上限 → 登录，返回 true 表示可继续 */
  function canSubmitGuard(): boolean {
    if (!session.value || session.value.status !== PREDICT_STATUS.ONGOING) return false;
    const {
      maxModifyCount,
      modifyCount,
      myVoteCode
    } = session.value;
    if (myVoteCode != null && maxModifyCount >= 0 && modifyCount >= maxModifyCount) return false;
    if (!isLoggedIn) {
      toLogin();
      return false;
    }
    return true;
  }

  /** 用户点击选项：与已选一致则忽略 → 公共校验 → 理由弹窗或直接提交 */
  function handleSelect(option: PredictOption) {
    if (!session.value) return;
    if (session.value.myVoteCode === option.code) return;
    if (!canSubmitGuard()) return;
    const cfg = session.value.reasonConfig;
    if (cfg && cfg.required && cfg.presetReasons && cfg.presetReasons.length > 0) {
      pendingOption.value = option;
      showReasonPopup.value = true;
      return;
    }
    doSubmit(option);
  }
  const currentStockPrice = computed(() => {
    if (!quoteSnapData.value) return '';
    return getDisplayPrice(quoteSnapData.value, 2);
  });
  const closePriceScalar = computed(() => {
    return session.value && session.value.answerSpec && session.value.answerSpec.scalar;
  });
  const closePricePrecision = computed(() => {
    const scalar = closePriceScalar.value;
    return scalar && scalar.precision != null ? scalar.precision : 3;
  });
  const closePriceMin = computed(() => {
    const scalar = closePriceScalar.value;
    return scalar && scalar.min != null ? scalar.min : 0;
  });
  const closePriceMax = computed(() => {
    const scalar = closePriceScalar.value;
    return scalar && scalar.max != null ? scalar.max : undefined;
  });

  /** 猜收盘价 CTA 点击：公共校验 → 打开输入弹窗 */
  function handleClosePriceCta() {
    if (!canSubmitGuard()) return;
    showClosePricePopup.value = true;
  }

  /** 猜收盘价提交 */
  async function handleClosePriceSubmit(value: string) {
    showClosePricePopup.value = false;
    if (!props.marketId || !session.value || submitting.value) return;
    submitting.value = true;
    try {
      const ok = await submit(props.marketId, value);
      if (ok) {
        session.value.myVoteCode = value;
        session.value.modifyCount++;
        lastClosePriceValue.value = value;
        refreshStats();
      }
    } finally {
      submitting.value = false;
    }
  }

  /** 理由弹窗确认后，携带理由提交，并记住选择 */
  function handleReasonSubmit(reason: {
    presetCode: string;
    label: Record<string, string>;
  }) {
    showReasonPopup.value = false;
    lastReasonCode.value = reason.presetCode;
    if (!pendingOption.value) return;
    doSubmit(pendingOption.value, reason);
    pendingOption.value = null;
  }

  /** 提交预测答案，成功后立即更新选中态 + 刷新统计数据 */
  async function doSubmit(option: PredictOption, reason?: {
    presetCode: string;
    label?: Record<string, string>;
  }) {
    if (!props.marketId || submitting.value) return;
    submitting.value = true;
    try {
      const isModify = session.value && session.value.myVoteCode != null;
      const ok = await submit(props.marketId, option.code, reason);
      if (ok && session.value) {
        session.value.myVoteCode = option.code;
        if (isModify) session.value.modifyCount++;
        setOptionVar(option.code);
        refreshStats();
      }
    } finally {
      submitting.value = false;
    }
  }

  /** 倒计时归零后重新拉取 session，驱动状态自动切换 */
  async function refreshSession() {
    if (!props.marketId) return;
    const detail = await apiClient.getMarketDetail(props.marketId);
    detail.status = detail.status.toLowerCase();
    if (session.value) {
      session.value.status = detail.status as PredictSession['status'];
      session.value.resolveAt = detail.resolveAt;
      session.value.correctValue = detail.correctValue || null;
    }
  }
  async function fetchWatchStatus(stockId: string) {
    const res = await fetchWatchStatusWithCache(stockId, stockIds => apiClient.getWatchStatus(stockIds));
    favorited.value = res.watchMap && res.watchMap[stockId] || false;
  }
  async function fetchQuoteSnapshot(securityId: number, lang: number) {
    const res = await fetchQuoteSnapshotWithCache(securityId, lang, (id, locale) => apiClient.getQuoteSnapshot(id, locale));
    quoteSnapData.value = res?.snapData || null;
  }

  /**
   * 重新拉取盘口统计（参与人数 + 各选项占比），
   * participantCount 统一从 stats 获取，所有类型都调用。
   */
  async function refreshStats() {
    if (!session.value || !props.marketId) return;
    const detail = session.value;
    const statsResult = await apiClient.getMarketStats(props.marketId);
    if (!statsResult) return;
    marketStats.value = statsResult;
    detail.participantCount = statsResult.participantCount;
    if (statsResult.optionCounts && detail.answerSpec && detail.answerSpec.options) {
      const total = statsResult.participantCount;
      const countMap: Record<string, number> = {};
      if (total > 0) {
        for (const item of statsResult.optionCounts) {
          countMap[item.answerValue] = item.count;
        }
      }
      const options = detail.answerSpec.options;
      if (total <= 0) {
        for (const opt of options) opt.percent = 0;
      } else {
        for (const opt of options) {
          const count = countMap[opt.code] || 0;
          opt.percent = Math.round(count / total * 10000) / 100;
        }
      }
    }
  }
  async function handleAddWatch() {
    if (!isLoggedIn) {
      toLogin();
      return;
    }
    if (!stockId.value) return;
    const res = await apiClient.addToWatchList([stockId.value]);
    if (res.success) {
      favorited.value = true;
      Toast.msg($t('watchAdded'));
    }
  }
  async function handleRemoveWatch() {
    if (!isLoggedIn) {
      toLogin();
      return;
    }
    if (!stockId.value) return;
    const res = await apiClient.removeFromWatchList([stockId.value]);
    if (res.success) {
      favorited.value = false;
      Toast.msg($t('watchRemoved'));
    }
  }
  function handleStockClick() {
    const url = browser.isMobileNN() ? props.stockLinkInternal : props.stockLinkExternal;
    if (url) {
      window.location.href = url;
    }
  }
  onMounted(async () => {
    const detail = await apiClient.getMarketDetail(props.marketId);
    detail.status = detail.status.toLowerCase();

    // Vue 2 对新增属性无法追踪响应式，先初始化 percent 再赋值给 ref
    if (detail.answerSpec && detail.answerSpec.options) {
      for (const opt of detail.answerSpec.options) {
        (opt as any).percent = 0;
      }
    }
    // Vue 2 同理：scalar.referencePrice 须在赋值 ref 前存在，否则后续写入时无法触发响应式
    if (detail.answerSpec && !detail.answerSpec.scalar) {
      (detail.answerSpec as any).scalar = {
        referencePrice: null
      };
    } else if (detail.answerSpec && detail.answerSpec.scalar && detail.answerSpec.scalar.referencePrice == null) {
      (detail.answerSpec.scalar as any).referencePrice = null;
    }

    // 后端字段 → 前端 PredictSession 字段映射
    // Vue 2 必须在赋值 ref 前初始化所有需要响应式追踪的属性
    const sess = detail as unknown as PredictSession;
    sess.deadline = detail.closeAt;
    sess.resolveAt = detail.resolveAt;
    sess.participantCount = 0;
    sess.myVoteCode = detail.myGuess ? detail.myGuess.answerValue : null;
    sess.settleStatus = detail.myGuess ? detail.myGuess.settleStatus : null;
    sess.dataSource = detail.dataSource || null;
    sess.correctValue = detail.correctValue || null;
    sess.maxModifyCount = detail.maxModifyCount ?? -1;
    sess.modifyCount = detail.myGuess ? detail.myGuess.modifyCount : 0;
    if (detail.myGuess && detail.myGuess.reason && detail.myGuess.reason.presetCode) {
      lastReasonCode.value = detail.myGuess.reason.presetCode;
    }
    if (detail.myGuess && props.kind === SESSION_KIND.STOCK_CLOSING_PRICE) {
      lastClosePriceValue.value = detail.myGuess.answerValue || null;
    }
    session.value = sess;

    // 用户有猜测记录时，将选项索引写入 fego 变量
    if (sess.myVoteCode) {
      setOptionVar(sess.myVoteCode);
    }
    const resolvedStockId = stockId.value;
    const shouldFetchStockInfo = shouldFetchStockRelatedInfo(props.kind, resolvedStockId);

    // 并行拉取统计数据和股票行情
    await Promise.all([refreshStats(), shouldFetchStockInfo ? fetchWatchStatus(resolvedStockId) : Promise.resolve(), shouldFetchStockInfo ? fetchQuoteSnapshot(Number(resolvedStockId), lang) : Promise.resolve()]);

    // 从 snapshot 提取昨收价，写入 session 供 close-price 组件消费
    if (props.kind === SESSION_KIND.STOCK_CLOSING_PRICE && quoteSnapData.value && session.value && session.value.answerSpec && session.value.answerSpec.scalar) {
      const snap = quoteSnapData.value as any;
      const raw = snap.last_close_price_10x9;
      if (raw != null) {
        (session.value.answerSpec.scalar as any).referencePrice = Number(raw) / 10 ** 9;
      }
    }
  });
  return {
    SESSION_KIND,
    $t,
    kls,
    lang,
    isPC,
    computedUpDownType,
    session,
    quoteSnapData,
    marketStats,
    favorited,
    showReasonPopup,
    lastReasonCode,
    showClosePricePopup,
    lastClosePriceValue,
    presetReasons,
    handleSelect,
    currentStockPrice,
    closePricePrecision,
    closePriceMin,
    closePriceMax,
    handleClosePriceCta,
    handleClosePriceSubmit,
    handleReasonSubmit,
    refreshSession,
    handleAddWatch,
    handleRemoveWatch,
    handleStockClick
  };
};
__sfc_main.components = Object.assign({
  UpDownSession,
  PredictStockInfo,
  ClosePriceSession,
  EventSession,
  ReasonPopup,
  ClosePricePopup
}, __sfc_main.components);
export default __sfc_main;
</script>

<style>
@tailwind base;
@tailwind components;
@tailwind utilities;

.is-en {
  font-family: 'BR Hendrix';
}
</style>
